import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { RichTextEditor, RichTextEditorConfig } from '@m/alchemy-ui';
import { CommunityContext } from '../../../context';
import { getValueOrPath, handleTranslationChange } from '../helpers/translation';
import { MixedTypes } from '../../../common/interfaces';
import { DEFAULT_LANGUAGE } from '../../shared-constants';
import { LanguageSelector } from './LanguageSelector';
import { StyledInput, Wrapper } from './TranslatableInput.styled';

interface TranslatableRTEProps {
  type: string;
  field: string;
  translateId: string;
  config?: RichTextEditorConfig;
}

function usePrevious<T>(value: T) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef<T>();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

export const TranslatableRTE = ({ type, field, translateId, config }: TranslatableRTEProps) => {
  const community = useContext(CommunityContext);
  const { values, setFieldValue } = useFormikContext<MixedTypes & { language: string }>();
  const { language: allLanguage } = values;

  const [language, setLanguage] = useState<string>(allLanguage || DEFAULT_LANGUAGE);
  const [fieldPath, setFieldPath] = useState<string>('');

  const isDefaultLanguage = language === DEFAULT_LANGUAGE;
  const previousSelectedLanguage = usePrevious(language);

  const initialDefaultvalue = isDefaultLanguage
    ? values[field as keyof MixedTypes]
    : getValueOrPath(values, { language, field, type, translateId });
  const [defaultValue, setDefaultValue] = useState(initialDefaultvalue);
  const [value, setValue] = useState<string | undefined>();

  useEffect(() => {
    const path = getValueOrPath(values, { language, field, type, translateId }, true);
    setFieldPath(path);
  }, [field, language, translateId, type, values]);

  useEffect(() => {
    setLanguage(allLanguage);
  }, [allLanguage]);

  // only update the default value when the language changes. otherwise the RTE can function in its usual non-controlled manner.
  // we use the default value as the key for the component. only when this key changes will the component remounted with new state.
  useEffect(() => {
    if (previousSelectedLanguage && language !== previousSelectedLanguage) {
      const value = isDefaultLanguage
        ? values[field as keyof MixedTypes]
        : getValueOrPath(values, { language, field, type, translateId });
      setDefaultValue(value);
    }
  }, [field, isDefaultLanguage, language, previousSelectedLanguage, translateId, type, values]);

  const handleChange = useCallback(
    (value: string) => {
      if (isDefaultLanguage) {
        setFieldValue(field, value);
        return;
      }

      if (language === previousSelectedLanguage || previousSelectedLanguage === DEFAULT_LANGUAGE) {
        handleTranslationChange({
          language,
          field,
          type,
          translateId,
          value,
          fieldPath,
          communityId: community.id,
          setFieldValue,
          values
        });
      }
    },
    [
      community.id,
      field,
      fieldPath,
      isDefaultLanguage,
      language,
      previousSelectedLanguage,
      setFieldValue,
      translateId,
      type,
      values
    ]
  );

  useEffect(() => {
    if (value || value === '') {
      handleChange(value);
    }
    // only trigger the change handler when RTE value is changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  /*
    Hacky solution to properly update the onChange handler:
    we are memoizing the config here with defaultValue in the deps array to properly update the onChange handler when RTE remounts with the new defaultValue. Passing an updated config when defaultValue is changed will trigger updating the 'mergedConfig' in the Alchemy 'Editor' component, which includes the onChange event handler for the editor.
    https://github.medallia.com/Alchemy/alchemy/blob/main/packages/alchemy-ui/src/form/rich-text-editor/Editor.tsx#L69
  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const RTEconfig = useMemo(() => ({ ...config }), [config, defaultValue, language]);

  const hideLanguageSelector =
    !community.options?.translationLanguages || community.options?.translationLanguages.length <= 1;

  return (
    <Wrapper>
      <StyledInput>
        <RichTextEditor defaultValue={defaultValue} config={RTEconfig} onChange={(value) => setValue(value)} />
      </StyledInput>
      {!hideLanguageSelector && (
        <LanguageSelector value={language} onLanguageChange={(lang: string) => setLanguage(lang)} />
      )}
    </Wrapper>
  );
};
